import classnames from 'classnames';

import { useLoadingState } from '@hh.ru/magritte-common-use-loading-state';
import {
    IconDynamicProps,
    IconProps,
    IconWrapperComponent,
    IconWrapperComponentSize16,
    IconWrapperComponentSize24,
    IconWrapperComponentSize32,
} from '@hh.ru/magritte-ui-icon/types';
import { Skeleton } from '@hh.ru/magritte-ui-skeleton';

import styles from './skeleton.less';

type IncompleteSkeletonProps = { loading?: boolean } & { Icon: IconWrapperComponent };

const STYLES_MAP = {
    16: styles.iconSkeletonSize16,
    24: styles.iconSkeletonSize24,
    32: styles.iconSkeletonSize32,
};

const getSkeletonProps = (iconProps: IconProps | IconDynamicProps, iconSize: 16 | 24 | 32) => {
    const withWrapper = !!iconProps.backgroundStyle || !!iconProps.borderStyle;
    const borderRadius = iconProps.borderRadius === 'halfHeight' ? 9999 : iconProps.borderRadius;
    const size = iconSize + (withWrapper ? (iconProps.padding || 0) * 2 : 0);
    const additionalClass = !withWrapper ? classnames(STYLES_MAP[iconSize], styles.iconSkeleton) : undefined;

    return { withWrapper, borderRadius, size, additionalClass };
};

export const SkeletonSize16 = (({ Icon, loading, ...iconProps }) => {
    const isLoading = useLoadingState() || loading;
    const { withWrapper, borderRadius, size, additionalClass } = getSkeletonProps(iconProps, 16);

    if (isLoading) {
        return (
            <div
                className={classnames(styles.iconSkeletonContainer)}
                style={{ padding: !withWrapper ? iconProps.padding : undefined }}
            >
                <Skeleton
                    loading={loading}
                    additionalClass={additionalClass}
                    width={size}
                    height={size}
                    borderRadius={withWrapper ? borderRadius : undefined}
                />
            </div>
        );
    }
    return <Icon {...iconProps} />;
}) as IconWrapperComponentSize16<IncompleteSkeletonProps>;

export const SkeletonSize24 = (({ Icon, loading, ...iconProps }) => {
    const isLoading = useLoadingState() || loading;
    const { withWrapper, borderRadius, size, additionalClass } = getSkeletonProps(iconProps, 24);
    if (isLoading) {
        return (
            <div
                className={classnames(styles.iconSkeletonContainer)}
                style={{ padding: !withWrapper ? iconProps.padding : undefined }}
            >
                <Skeleton
                    loading={loading}
                    additionalClass={additionalClass}
                    width={size}
                    height={size}
                    borderRadius={withWrapper ? borderRadius : undefined}
                />
            </div>
        );
    }

    return <Icon {...iconProps} />;
}) as IconWrapperComponentSize24<IncompleteSkeletonProps>;

export const SkeletonSize32 = (({ Icon, loading, ...iconProps }) => {
    const isLoading = useLoadingState() || loading;
    const { withWrapper, borderRadius, size, additionalClass } = getSkeletonProps(iconProps, 32);
    if (isLoading) {
        return (
            <div
                className={classnames(styles.iconSkeletonContainer)}
                style={{ padding: !withWrapper ? iconProps.padding : undefined }}
            >
                <Skeleton
                    loading={loading}
                    additionalClass={additionalClass}
                    width={size}
                    height={size}
                    borderRadius={withWrapper ? borderRadius : undefined}
                />
            </div>
        );
    }

    return <Icon {...iconProps} />;
}) as IconWrapperComponentSize32<IncompleteSkeletonProps>;

SkeletonSize16.isIcon = true;
SkeletonSize24.isIcon = true;
SkeletonSize32.isIcon = true;
